import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ScreenTitle } from "@/components/ScreenTitle";
import { useSite } from "@/context/SiteContext";
import { Plus, Trash2, Save, Globe, Power, PowerOff, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Multi-site management. Each site = its own WordPress + prompt override.
 * The "current site" state is held in SiteContext (localStorage) and is used
 * across QueuePage / FeedsPage / Refresh.
 */
export default function SitesPage() {
  const { reload: reloadSites, currentId } = useSite();
  const [sites, setSites] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", group: "", wp_site_url: "" });
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    const r = await api.get("/sites");
    setSites(r.data);
  };
  useEffect(() => { load(); }, []);

  const createSite = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await api.post("/sites", { name: addForm.name.trim(), group: addForm.group || null, wp_site_url: addForm.wp_site_url });
      toast.success("Site créé — pense à renseigner ses identifiants WordPress");
      setAddForm({ name: "", group: "", wp_site_url: "" });
      setShowAdd(false);
      load(); reloadSites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    }
  };

  const saveSite = async (s, patch) => {
    setSavingId(s.id);
    try {
      await api.patch(`/sites/${s.id}`, patch);
      toast.success("Enregistré");
      load(); reloadSites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sauvegarde impossible");
    } finally { setSavingId(null); }
  };

  const removeSite = async (s) => {
    if (!window.confirm(
      `Supprimer le site "${s.name}" ?\n\n` +
      `Les flux qui lui étaient rattachés seront désassignés (mais pas supprimés). ` +
      `Les brouillons déjà générés pour ce site restent en base.`
    )) return;
    try {
      await api.delete(`/sites/${s.id}`);
      toast.success("Site supprimé");
      load(); reloadSites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  return (
    <div data-testid="sites-page">
      <ScreenTitle
        eyebrow={`${sites.length} site(s)`}
        title="Sites WordPress"
        right={
          <button onClick={() => setShowAdd((v) => !v)} data-testid="site-add-toggle"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-black hover:bg-black hover:text-white">
            {showAdd ? <ChevronUp className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
        }
      />

      {showAdd && (
        <form onSubmit={createSite} className="mx-5 mb-4 p-4 border border-black bg-white space-y-2" data-testid="site-add-form">
          <input required placeholder="Nom du site (ex: Entrevue)" value={addForm.name}
            onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
            data-testid="site-new-name"
            className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm focus:outline-none focus:border-black" />
          <input placeholder="Regroupement (optionnel, ex: Presse grand public)" value={addForm.group}
            onChange={(e) => setAddForm({ ...addForm, group: e.target.value })}
            className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm focus:outline-none focus:border-black" />
          <input type="url" placeholder="URL WordPress (https://…)" value={addForm.wp_site_url}
            onChange={(e) => setAddForm({ ...addForm, wp_site_url: e.target.value })}
            className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm focus:outline-none focus:border-black" />
          <button type="submit" data-testid="site-new-save"
            className="w-full min-h-[44px] flex items-center justify-center gap-2 bg-black text-white hover:bg-[#222] text-[11px] uppercase tracking-widest font-mono">
            <Plus className="w-4 h-4" /> Créer le site
          </button>
        </form>
      )}

      <div className="px-5 pb-5 space-y-3">
        {sites.map((s) => {
          const isOpen = openId === s.id;
          return (
            <section key={s.id} className={`border ${s.id === currentId ? "border-black" : "border-[var(--border-default)]"} bg-white`}
                     data-testid={`site-row-${s.id}`}>
              <header className="p-3 flex items-start gap-3">
                <Globe className={`w-4 h-4 mt-1 ${s.active ? "text-black" : "text-[var(--text-muted)]"}`} strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                    {s.group || "sans regroupement"} · {s.feed_count} flux · {s.wp_app_password_set ? "wp configuré" : "wp incomplet"}
                    {s.id === currentId && " · site actif"}
                  </div>
                  <div className="font-serif text-lg">{s.name}</div>
                  <div className="text-xs font-mono text-[var(--text-muted)] break-all">{s.wp_site_url || "—"}</div>
                </div>
                <button onClick={() => setOpenId(isOpen ? null : s.id)}
                  data-testid={`site-toggle-${s.id}`}
                  className="min-h-[36px] px-3 border border-black hover:bg-black hover:text-white text-[11px] font-mono uppercase tracking-widest">
                  {isOpen ? "Fermer" : "Éditer"}
                </button>
              </header>
              {isOpen && (
                <SiteEditor site={s} onSave={(patch) => saveSite(s, patch)}
                  onDelete={() => removeSite(s)}
                  saving={savingId === s.id} />
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function SiteEditor({ site, onSave, onDelete, saving }) {
  const [form, setForm] = useState({
    name: site.name, group: site.group || "", wp_site_url: site.wp_site_url || "",
    wp_username: site.wp_username || "", wp_app_password: "",
    wp_default_author_id: site.wp_default_author_id || 1,
    wp_default_category_id: site.wp_default_category_id || "",
    prompt_template: site.prompt_template || "",
    max_item_age_hours: site.max_item_age_hours ?? "",
    active: site.active,
  });
  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="border-t border-[var(--border-default)] p-4 space-y-3 bg-[var(--bg-app)]"
         data-testid={`site-editor-${site.id}`}>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Nom</span>
          <input value={form.name} onChange={handle("name")}
            className="mt-1 w-full min-h-[40px] border border-[var(--border-default)] px-3 text-sm bg-white" />
        </label>
        <label className="text-xs">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Regroupement</span>
          <input value={form.group} onChange={handle("group")} placeholder="ex: Presse pro"
            className="mt-1 w-full min-h-[40px] border border-[var(--border-default)] px-3 text-sm bg-white" />
        </label>
      </div>
      <label className="text-xs block">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">URL WordPress</span>
        <input type="url" value={form.wp_site_url} onChange={handle("wp_site_url")}
          className="mt-1 w-full min-h-[40px] border border-[var(--border-default)] px-3 text-sm font-mono bg-white" />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Utilisateur WP</span>
          <input value={form.wp_username} onChange={handle("wp_username")}
            className="mt-1 w-full min-h-[40px] border border-[var(--border-default)] px-3 text-sm font-mono bg-white" />
        </label>
        <label className="text-xs">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
            Mot de passe application {site.wp_app_password_set && "(saisir pour changer)"}
          </span>
          <input type="password" value={form.wp_app_password} onChange={handle("wp_app_password")}
            placeholder={site.wp_app_password_set ? "•••• configuré ••••" : "xxxx xxxx xxxx xxxx"}
            className="mt-1 w-full min-h-[40px] border border-[var(--border-default)] px-3 text-sm font-mono bg-white" />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Auteur par défaut (ID)</span>
          <input type="number" value={form.wp_default_author_id} onChange={handle("wp_default_author_id")}
            className="mt-1 w-full min-h-[40px] border border-[var(--border-default)] px-3 text-sm font-mono bg-white" />
        </label>
        <label className="text-xs">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Catégorie par défaut (ID, optionnel)</span>
          <input type="number" value={form.wp_default_category_id} onChange={handle("wp_default_category_id")}
            className="mt-1 w-full min-h-[40px] border border-[var(--border-default)] px-3 text-sm font-mono bg-white" />
        </label>
      </div>
      <label className="text-xs block">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
          Fenêtre de fraîcheur (heures, vide = défaut global 24h)
        </span>
        <input type="number" min="1" max="720" value={form.max_item_age_hours}
          onChange={handle("max_item_age_hours")}
          placeholder="24"
          data-testid={`site-max-age-${site.id}`}
          className="mt-1 w-40 min-h-[40px] border border-[var(--border-default)] px-3 text-sm font-mono bg-white" />
        <span className="ml-2 text-[10px] text-[var(--text-muted)]">
          Ex : 72 pour un média qui publie peu (Outre-mer, hebdo…)
        </span>
      </label>
      <label className="text-xs block">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
          Prompt LLM propre au site (vide = fallback prompt global)
        </span>
        <textarea rows={10} value={form.prompt_template} onChange={handle("prompt_template")}
          data-testid={`site-prompt-${site.id}`}
          className="mt-1 w-full border border-[var(--border-default)] px-3 py-2 text-xs font-mono bg-white" />
      </label>
      <div className="flex flex-wrap gap-2 pt-1">
        <button onClick={() => onSave({
            name: form.name, group: form.group || null,
            wp_site_url: form.wp_site_url,
            wp_username: form.wp_username,
            wp_app_password: form.wp_app_password,  // empty string ignored server-side
            wp_default_author_id: parseInt(form.wp_default_author_id, 10) || 1,
            wp_default_category_id: form.wp_default_category_id === "" ? null : parseInt(form.wp_default_category_id, 10),
            prompt_template: form.prompt_template,
            max_item_age_hours: form.max_item_age_hours === "" ? null : parseInt(form.max_item_age_hours, 10),
            active: form.active,
          })} disabled={saving} data-testid={`site-save-${site.id}`}
          className="min-h-[44px] px-4 flex items-center gap-2 bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-60">
          <Save className="w-4 h-4" /> Enregistrer
        </button>
        <button onClick={() => onSave({ active: !form.active })} disabled={saving}
          data-testid={`site-toggle-active-${site.id}`}
          className="min-h-[44px] px-4 flex items-center gap-2 border border-[var(--border-default)] text-[11px] uppercase tracking-widest font-mono hover:border-black">
          {form.active ? <><PowerOff className="w-4 h-4" /> Désactiver</> : <><Power className="w-4 h-4" /> Activer</>}
        </button>
        <button onClick={onDelete} disabled={saving} data-testid={`site-delete-${site.id}`}
          className="min-h-[44px] px-4 flex items-center gap-2 border border-[var(--accent-strong)] text-[var(--accent-strong)] text-[11px] uppercase tracking-widest font-mono hover:bg-[var(--accent-strong)] hover:text-white">
          <Trash2 className="w-4 h-4" /> Supprimer
        </button>
      </div>
    </div>
  );
}
